import { Phone, ArrowRight, ShieldCheck, MapPin, HeartHandshake } from "lucide-react";
import { CLINIC } from "../data/clinic";
import { LOGO_DARK } from "../data/assets";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_foot-health-pro-2/artifacts/zceq9uw9_IMG_1949.jpeg";

export default function Hero() {
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section data-testid="hero-section" className="relative pt-28 lg:pt-32 pb-14 lg:pb-24 overflow-hidden">
      {/* Decorative background glows */}
      <div className="absolute inset-0 lfc-grain opacity-90 pointer-events-none" />
      <div className="absolute top-20 -left-32 w-96 h-96 rounded-full bg-[#2BB8B4]/15 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#7B46D9]/20 blur-[140px] pointer-events-none" />

      <div className="lfc-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7 lfc-fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2D1456]/70 border border-[#4A2D7A] backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-[#2BB8B4] lfc-pulse-soft" />
              <span className="lfc-eyebrow">Qualified · Insured · Mobile</span>
            </div>

            <h1 className="lfc-h1 mt-7" data-testid="hero-headline">
              Professional foot care,<br className="hidden md:block" /> brought to your{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-white">front door.</span>
                <span className="absolute left-0 right-0 bottom-1.5 h-3 bg-[#2BB8B4]/30 -z-0 rounded-sm blur-[2px]"></span>
              </span>
            </h1>

            <p className="mt-7 text-lg lg:text-xl text-[#C2B4E6] max-w-2xl leading-relaxed">
              At {CLINIC.name}, we deliver compassionate, personalised foot care in the comfort of your own home —
              ideal for elderly patients, those with reduced mobility, or anyone who values stress-free, expert treatment.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-book-cta"
                onClick={() => goTo("contact")}
                className="lfc-btn-primary"
              >
                Book a home visit
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
              <a
                data-testid="hero-call-cta"
                href={`tel:${CLINIC.phoneRaw}`}
                className="lfc-btn-secondary"
              >
                <Phone className="w-4 h-4" strokeWidth={1.6} />
                {CLINIC.phone}
              </a>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Pillar icon={ShieldCheck} title="Fully insured" sub="Qualified Foot Health Practitioner" />
              <Pillar icon={HeartHandshake} title="Patient-centred" sub="Care tailored to you" />
              <Pillar icon={MapPin} title="Mobile across Scotland" sub="6 regions covered" />
            </div>
          </div>

          {/* Right: image stack */}
          <div className="lg:col-span-5 lfc-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative">
              <div className="absolute -top-8 -right-6 w-48 h-48 rounded-full bg-[#2BB8B4]/30 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-10 -left-8 w-56 h-56 rounded-full bg-[#7B46D9]/35 blur-3xl pointer-events-none" />

              {/* Logo emblem floating */}
              <div className="absolute -top-8 -left-8 z-20 hidden md:block">
                <div className="grid place-items-center w-32 h-32 rounded-3xl bg-[#2D1456] ring-1 ring-[#2BB8B4]/40 lfc-glow-teal p-2.5 rotate-[-6deg]">
                  <img src={LOGO_DARK} alt="Litisha logo" className="w-full h-full object-contain" data-testid="hero-logo" />
                </div>
              </div>

              <div className="relative rounded-[2rem] overflow-hidden border border-[#4A2D7A] bg-[#2D1456]">
                <img
                  src={HERO_IMG}
                  alt="Caring foot care professional treating a patient at home"
                  className="w-full h-[460px] lg:h-[560px] object-cover opacity-95 lfc-kenburns"
                  data-testid="hero-image"
                />
                {/* purple overlay to keep tone consistent */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#1B0B3A]/55 via-transparent to-[#2BB8B4]/10 pointer-events-none" />

                <div className="absolute bottom-5 left-5 right-5 bg-[#1B0B3A]/85 backdrop-blur-md rounded-2xl border border-[#4A2D7A] p-4 flex items-center gap-4 shadow-lg">
                  <div className="w-11 h-11 rounded-full bg-[#2BB8B4] grid place-items-center text-[#0E0524]">
                    <HeartHandshake className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-white" style={{ fontFamily: "Outfit" }}>
                      Trusted in-home care
                    </div>
                    <div className="text-[13px] text-[#C2B4E6]">Friendly · Hygienic · Professional</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pillar({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3">
      <div className="grid place-items-center w-10 h-10 rounded-xl bg-[#2D1456] border border-[#4A2D7A] text-[#5FE6E0] shrink-0">
        <Icon className="w-5 h-5" strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-[15px] font-semibold text-white" style={{ fontFamily: "Outfit" }}>
          {title}
        </div>
        <div className="text-[13.5px] text-[#C2B4E6]">{sub}</div>
      </div>
    </div>
  );
}
